import { keyframes, Theme } from '@emotion/react'
import { Components } from '@mui/material'

import { shadows } from './shadows'
import { CustomPaletteOptions } from './types'

const glowAnimation = keyframes`
0%, 100% {
  background-color: #A6192E;
  color: #fff
}
50% {
  background-color: #FFFFFF;
  color: #222221
}
`

export const components = (
  palette: CustomPaletteOptions
): Components<Omit<Theme, 'components'>> | undefined => {
  const boxShadow = shadows('light')[1]

  return {
    MuiCard: {
      styleOverrides: {
        root: {
          border: `1px solid ${palette.yellow?.main}`,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: palette.black?.[20],
          opacity: 1,
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          backgroundColor: palette.yellow?.main,
        },
        indicator: {
          background: palette.black?.main,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          color: palette.black?.main,
          fontWeight: '600',
          '&.Mui-selected': {
            color: palette.black?.main,
            backgroundColor: palette.yellow?.main,
          },
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          background: `${palette.yellow?.main} !important`,
          backgroundColor: `${palette.yellow?.main} !important`,
          borderColor: `${palette.yellow?.main} !important`,
          color: `${palette.black?.main} !important`,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          color: `${palette.black?.main} !important`,
          background: `${palette.yellow?.main} !important`,
          borderColor: `${palette.yellow?.main} !important`,
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          color: `${palette.black?.main}`,
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          color: `${palette.black?.main} !important`,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          color: palette.black?.main,
          '&:hover': {
            backgroundColor: palette.yellow?.[20],
          },
          '&.Mui-selected': {
            color: palette.black?.main,
            backgroundColor: palette.yellow?.main,
            '&:hover': {
              backgroundColor: palette.yellow?.main,
            },
          },
        },
      },
    },
    MuiList: {
      styleOverrides: {
        root: () => {
          return {
            borderRadius: '8px',
            backgroundColor: `${palette.common?.white} !important`,
          }
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          background: palette.common?.white,
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        barColorPrimary: {
          backgroundColor: palette.yellow?.main,
        },
        colorPrimary: {
          backgroundColor: palette.common?.white,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          transition: 'background-color .5s ease',
          borderRadius: '8px',
          fontSize: '0.875rem',
          boxShadow: boxShadow,
          '&.MuiButton-containedPrimary': {
            backgroundColor: palette.yellow?.main,
            fontWeight: '600',
            color: palette.black?.main,
            border: `2px solid ${palette.yellow?.main}`,
            '&:hover': {
              backgroundColor: palette.common?.white,
              border: `2px solid ${palette.yellow?.main}`,
            },
          },
          '&.MuiButton-containedWarning': {
            backgroundColor: palette.red?.main,
          },
          '&.MuiButton-outlinedPrimary  ': {
            backgroundColor: palette.black?.main,
            color: palette.common?.white,
            border: `2px solid ${palette.black?.main}`,
            fontWeight: '600',
            '&:hover': {
              backgroundColor: palette.common?.white,
              color: palette.black?.main,
            },
          },
          '&.MuiButton-outlinedSecondary': {
            backgroundColor: palette.common?.white,
            color: palette.black?.main,
            fontWeight: '600',
            border: `2px solid ${palette.black?.main}`,
            '&:hover': {
              borderColor: palette.black?.[20],
            },
          },
          '&.MuiButton-glowPrimary': {
            animation: `${glowAnimation} 2s infinite`,
            border: `2px solid ${palette.red?.main}`,
            // transition: theme.transitions.create(['background-color', 'transform'], {
            //   duration: theme.transitions.duration.standard,
            // }),
            // '&:hover': {
            //   backgroundColor: palette.background?.paper,
            // },
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          borderColor: palette.black?.[50],
        },
        input: {
          backgroundColor: palette.common?.white,
          boxShadow: boxShadow,
          borderRadius: '8px',
        },
        root: {
          borderRadius: '8px',
          backgroundColor: palette.common?.white,
          borderColor: palette.black?.[50],
          boxShadow: boxShadow,
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: palette.yellow?.main,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: palette.yellow?.main,
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          fontFamily: 'inherit',
          '&:-webkit-autofill': {
            WebkitBoxShadow: '0 0 0 1000px white inset',
            WebkitTextFillColor: palette.black?.main,
            borderColor: palette.black?.[50],
          },
          '&:-webkit-autofill:hover': {
            WebkitBoxShadow: '0 0 0 1000px white inset',
            WebkitTextFillColor: palette.black?.main,
            borderColor: palette.black?.[50],
          },
          '&:-webkit-autofill:focus': {
            WebkitBoxShadow: '0 0 0 1000px white inset',
            WebkitTextFillColor: palette.black?.main,
            borderColor: palette.black?.[50],
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          '&.Mui-focused': {
            color: palette.black?.main,
            backgroundColor: palette.common?.white,
          },
        },
        shrink: {
          color: palette.black?.main,
          backgroundColor: palette.common?.white,
        },
      },
    },
  }
}
